import { useState, useRef, useCallback, useEffect } from "react";

import {
  VIDEO_CONSTRAINTS,
  AUDIO_CONSTRAINTS,
  STORAGE_KEYS,
  TIMING,
} from "@/constants";
import type { MediaStatus } from "@/types";

// Re-export for convenience
export type { MediaStatus };

type CameraFacingMode = "user" | "environment";

function loadCameraFacingMode(): CameraFacingMode {
  if (typeof window === "undefined") return "user";
  return localStorage.getItem(STORAGE_KEYS.cameraFacingMode) === "environment"
    ? "environment"
    : "user";
}

function saveCameraFacingMode(facingMode: CameraFacingMode): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.cameraFacingMode, facingMode);
}

interface UseMediaDevicesReturn {
  // Stream
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;

  // Status
  cameraStatus: MediaStatus;
  micStatus: MediaStatus;
  error: string | null;

  // Audio level (0-100)
  audioLevel: number;

  // Video/Audio State
  isVideoEnabled: boolean;

  // Actions
  startVideoAndAudio: () => Promise<void>;
  startAudioOnly: () => Promise<void>;
  stopAll: () => void;
  // Camera switching
  videoDevices: MediaDeviceInfo[];
  switchCamera: () => Promise<void>;
}

/**
 * Hook for managing camera and microphone access
 *
 * SIMPLIFIED: Once started, stream mode is fixed.
 * User can only stop and restart in a different mode.
 */
export function useMediaDevices(): UseMediaDevicesReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraStatus, setCameraStatus] = useState<MediaStatus>("idle");
  const [micStatus, setMicStatus] = useState<MediaStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  // Camera switching state
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentFacingMode, setCurrentFacingMode] =
    useState<CameraFacingMode>(loadCameraFacingMode);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Track the audio track ID being monitored to avoid unnecessary restarts
  const monitoredAudioTrackIdRef = useRef<string | null>(null);

  // ============================================================
  // Audio Level Monitoring
  // ============================================================

  /**
   * Stop audio monitoring and cleanup resources
   */
  const stopAudioMonitoring = useCallback(async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== "closed") {
          await audioContextRef.current.close();
        }
      } catch (e) {
        console.warn("[useMediaDevices] Error closing AudioContext:", e);
      }
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    monitoredAudioTrackIdRef.current = null;
    setAudioLevel(0);
  }, []);

  const startAudioMonitoring = useCallback(
    async (mediaStream: MediaStream) => {
      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length === 0) {
        console.warn("[useMediaDevices] No audio tracks in stream!");
        return;
      }

      const firstAudioTrack = audioTracks[0];
      if (!firstAudioTrack) {
        console.warn("[useMediaDevices] No audio track found!");
        return;
      }

      const currentAudioTrackId = firstAudioTrack.id;

      // Skip if we're already monitoring the same audio track
      if (
        monitoredAudioTrackIdRef.current === currentAudioTrackId &&
        audioContextRef.current
      ) {
        console.log(
          "[useMediaDevices] Audio track unchanged, skipping monitoring restart",
        );
        return;
      }

      console.log(
        "[useMediaDevices] Starting audio monitoring for track:",
        currentAudioTrackId,
      );

      try {
        // Stop any existing monitoring first
        await stopAudioMonitoring();

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(mediaStream);

        // Create a gain node with zero gain to keep pipeline active without sound
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0;

        analyser.fftSize = TIMING.audioAnalyserFFTSize;
        analyser.smoothingTimeConstant = TIMING.audioSmoothingConstant;

        // Connect: source -> analyser -> gain (0) -> destination
        source.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(audioContext.destination);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        monitoredAudioTrackIdRef.current = currentAudioTrackId;

        // Start analysis loop
        const resumeAndStart = async () => {
          if (audioContext.state === "suspended") {
            await audioContext.resume();
          }

          const dataArray = new Uint8Array(analyser.frequencyBinCount);

          const updateLevel = () => {
            if (!analyserRef.current || !audioContextRef.current) {
              return;
            }

            analyserRef.current.getByteFrequencyData(dataArray);

            // Calculate average volume
            const average =
              dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            const normalizedLevel = Math.min(
              100,
              Math.round((average / 128) * 100),
            );

            setAudioLevel(normalizedLevel);
            animationFrameRef.current = requestAnimationFrame(updateLevel);
          };

          updateLevel();
          console.log("[useMediaDevices] Audio monitoring started");
        };

        resumeAndStart();
      } catch (err) {
        console.error(
          "[useMediaDevices] Error starting audio monitoring:",
          err,
        );
      }
    },
    [stopAudioMonitoring],
  );

  // ============================================================
  // Device Management
  // ============================================================

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      setVideoDevices(cameras);
      console.log(
        "[useMediaDevices] Found cameras:",
        cameras.map((c) => c.label || c.deviceId),
      );
    } catch (err) {
      console.error("[useMediaDevices] Error enumerating devices:", err);
    }
  }, []);

  // Monitor device changes
  useEffect(() => {
    enumerateDevices();
    navigator.mediaDevices.addEventListener("devicechange", enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        enumerateDevices,
      );
    };
  }, [enumerateDevices]);

  // ============================================================
  // Stream Management
  // ============================================================

  /**
   * Handle media errors consistently
   */
  const handleMediaError = useCallback(
    (err: unknown, isVideoRequest: boolean) => {
      console.error("[useMediaDevices] Media error:", err);

      if (err instanceof DOMException) {
        switch (err.name) {
          case "NotAllowedError":
            if (isVideoRequest) {
              setCameraStatus("denied");
              setMicStatus("denied");
            } else {
              setMicStatus("denied");
            }
            setError(
              "Camera/microphone access denied. Please allow in browser settings.",
            );
            break;
          case "NotFoundError":
            if (isVideoRequest) {
              setCameraStatus("error");
              setMicStatus("error");
            } else {
              setMicStatus("error");
            }
            setError("No camera or microphone found.");
            break;
          case "NotReadableError":
            if (isVideoRequest) {
              setCameraStatus("error");
              setMicStatus("error");
            } else {
              setMicStatus("error");
            }
            setError("Camera/microphone already in use by another app.");
            break;
          default:
            if (isVideoRequest) {
              setCameraStatus("error");
              setMicStatus("error");
            } else {
              setMicStatus("error");
            }
            setError(`Media error: ${err.message}`);
        }
      } else {
        if (isVideoRequest) {
          setCameraStatus("error");
          setMicStatus("error");
        } else {
          setMicStatus("error");
        }
        setError("An unexpected error occurred.");
      }
    },
    [],
  );

  /**
   * Start video and audio streaming
   */
  const startVideoAndAudio = useCallback(async () => {
    console.log("[useMediaDevices] Starting Video + Audio");
    setCameraStatus("requesting");
    setMicStatus("requesting");
    setError(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...VIDEO_CONSTRAINTS,
          facingMode: currentFacingMode,
        },
        audio: AUDIO_CONSTRAINTS,
      });

      console.log(
        "[useMediaDevices] Video+Audio stream obtained:",
        mediaStream.getTracks().map((t) => t.kind),
      );

      setStream(mediaStream);
      setCameraStatus("active");
      setMicStatus("active");
      setIsVideoEnabled(true);
    } catch (err) {
      handleMediaError(err, true);
    }
  }, [currentFacingMode, handleMediaError]);

  /**
   * Switch between front and back camera using facingMode
   * Based on: https://gosink.in/webrtc-switch-cameras-using-javascript-getusermedia/
   */
  const switchCamera = useCallback(async () => {
    if (videoDevices.length < 2) {
      console.log("[useMediaDevices] No other cameras available");
      return;
    }

    if (!stream || !isVideoEnabled) {
      console.warn("[useMediaDevices] Cannot switch camera: video not active");
      return;
    }

    // Toggle facing mode
    const newFacingMode = currentFacingMode === "user" ? "environment" : "user";

    console.log(
      `[useMediaDevices] Switching camera from ${currentFacingMode} to ${newFacingMode}`,
    );

    try {
      // IMPORTANT: Stop old video tracks BEFORE getting new stream
      // This releases the camera so the new stream can access a different camera
      const oldVideoTracks = stream.getVideoTracks();
      oldVideoTracks.forEach((track) => {
        console.log(
          `[useMediaDevices] Stopping old video track: ${track.label}`,
        );
        track.stop();
      });

      // Keep reference to audio tracks (they remain active)
      const currentAudioTracks = stream.getAudioTracks();

      // Get new video stream with the new facing mode
      const newVideoStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      const newVideoTrack = newVideoStream.getVideoTracks()[0];
      if (!newVideoTrack) {
        throw new Error("No video track found in new stream");
      }

      console.log(
        `[useMediaDevices] Got new video track: ${newVideoTrack.label}`,
      );

      // Create new stream with existing audio and new video
      const newStream = new MediaStream([...currentAudioTracks, newVideoTrack]);

      // Update state
      setStream(newStream);
      setCurrentFacingMode(newFacingMode);
      saveCameraFacingMode(newFacingMode);

      console.log(
        `[useMediaDevices] Camera switched successfully to ${newFacingMode}`,
      );
    } catch (err) {
      console.error("[useMediaDevices] Error switching camera:", err);
      // If switching fails, try to recover with the original facing mode
      setError(
        `Failed to switch camera: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    }
  }, [videoDevices.length, currentFacingMode, stream, isVideoEnabled]);

  /**
   * Start audio only streaming (no video)
   */
  const startAudioOnly = useCallback(async () => {
    console.log("[useMediaDevices] Starting Audio Only");
    setMicStatus("requesting");
    setCameraStatus("idle");
    setError(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: AUDIO_CONSTRAINTS,
      });

      console.log(
        "[useMediaDevices] Audio-only stream obtained:",
        mediaStream.getTracks().map((t) => t.kind),
      );

      setStream(mediaStream);
      setMicStatus("active");
      setIsVideoEnabled(false);
    } catch (err) {
      handleMediaError(err, false);
    }
  }, [handleMediaError]);

  /**
   * Stop all streaming and release resources
   */
  const stopAll = useCallback(() => {
    console.log("[useMediaDevices] Stopping all media");

    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
        console.log(`[useMediaDevices] Stopped track: ${track.kind}`);
      });
      setStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    stopAudioMonitoring();
    setCameraStatus("idle");
    setMicStatus("idle");
    setIsVideoEnabled(false);
    setError(null);
  }, [stream, stopAudioMonitoring]);

  // ============================================================
  // Effects
  // ============================================================

  // The video element is mounted only after streaming starts. Attach the
  // stream after React has rendered it so the baby device gets a local preview.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;

    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }
    video.muted = true;

    void video.play().catch((err) => {
      console.warn("[useMediaDevices] Local preview play failed:", err);
    });
  }, [stream]);

  // Start/stop audio monitoring based on stream
  useEffect(() => {
    if (stream) {
      startAudioMonitoring(stream);
    }
  }, [stream, startAudioMonitoring]);

  // Track stream in ref for unmount cleanup (don't use stream as dependency!)
  const streamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  // Cleanup on unmount ONLY (empty dependency array)
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      stopAudioMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only runs on unmount

  return {
    stream,
    videoRef,
    cameraStatus,
    micStatus,
    error,
    audioLevel,
    isVideoEnabled,
    startVideoAndAudio,
    startAudioOnly,
    stopAll,
    videoDevices,
    switchCamera,
  };
}
